import { allBuckets, MaterialityMatrixLevel, MatrixMatter, MatrixMattersByBucket, MatterBucketId } from './types';

export function getMatterBucketId(imLevel: MaterialityMatrixLevel, fmLevel: MaterialityMatrixLevel): MatterBucketId {
  return `im-${imLevel}:fm-${fmLevel}`;
}

export function groupMattersByBucket(matters: MatrixMatter[]) {
  // Prepare an object with every possible bucket as keys and empty arrays as values.
  const grouped = allBuckets.reduce((acc, bucket) => {
    acc[bucket] = [];
    return acc;
  }, {} as MatrixMattersByBucket);

  // Push each matter to all the buckets that match its im and fm levels
  matters.forEach(matter => {
    const { positive, negative } = matter;

    if (!positive?.isMaterial && !negative?.isMaterial) {
      grouped[getMatterBucketId('low', 'low')].push(matter);
      return;
    }

    if (positive && negative && positive.imLevel === negative.imLevel && positive.fmLevel === negative.fmLevel) {
      grouped[getMatterBucketId(positive.imLevel, positive.fmLevel)].push(matter);
      return;
    }

    if (positive?.isMaterial) {
      grouped[getMatterBucketId(positive.imLevel, positive.fmLevel)].push({ ...matter, negative: null });
    }

    if (negative?.isMaterial) {
      grouped[getMatterBucketId(negative.imLevel, negative.fmLevel)].push({ ...matter, positive: null });
    }
  });

  return grouped;
}

/**
 * The order of the matters inside one matrix cell: highest impact materiality
 * score first, a matter placed with both valences ranked by the higher of the
 * two. Shared by the platform's matrix and the chat matrix card, so the two list
 * a cell in the same order.
 */
export function compareMatrixMattersInCell(a: MatrixMatter, b: MatrixMatter): -1 | 0 | 1 {
  const aScore = getMatrixMatterSortScore(a);
  const bScore = getMatrixMatterSortScore(b);
  if (aScore > bScore) return -1;
  if (aScore < bScore) return 1;
  return 0;
}

function getMatrixMatterSortScore(matter: MatrixMatter) {
  const { positive, negative } = matter;

  if (positive && negative) {
    return Math.max(positive.imScore, negative.imScore);
  }
  if (positive) return positive.imScore;
  if (negative) return negative.imScore;
  return 0;
}
